"use client";

import { Box, Flex, Grid, Icon, Text } from "@chakra-ui/react";
import { useTranslations } from "next-intl";
import {
  TbBrain,
  TbDatabaseSearch,
  TbReportAnalytics,
  TbUsersGroup,
  TbLayoutDashboard,
  TbLock,
} from "react-icons/tb";
import { IconType } from "react-icons";
import { WistiaPlayer } from "@wistia/wistia-player-react";
import { WISTIA_MEDIA_ID } from "@/_components/ui/GroupCandidates/constant";
import { HELP_CENTER_IDS } from "../_constants/help-center.ids";

const FEATURE_ICONS: IconType[] = [
  TbBrain,
  TbDatabaseSearch,
  TbReportAnalytics,
  TbUsersGroup,
  TbLayoutDashboard,
  TbLock,
];

export function GuideSection() {
  const t = useTranslations("landing.help_center.guide");

  return (
    <Box id={HELP_CENTER_IDS.guide.section}>
      {/* Introduction */}
      <Box
        id="guide-intro"
        data-debug-id={HELP_CENTER_IDS.guide.introDebug}
        scrollMarginTop="80px"
      >
        <Flex
          id={HELP_CENTER_IDS.guide.introLabelRow}
          align="center"
          gap={2}
          mb={3}
        >
          <Box
            id={HELP_CENTER_IDS.guide.introLabelBar}
            w="20px"
            h="0.5"
            bg="primary.600"
            borderRadius="1px"
          />
          <Text
            id={HELP_CENTER_IDS.guide.introLabel}
            fontSize="11px"
            fontWeight="bold"
            letterSpacing="0.12em"
            textTransform="uppercase"
            color="primary.600"
          >
            {t("intro.label")}
          </Text>
        </Flex>

        <Text
          id={HELP_CENTER_IDS.guide.introTitle}
          fontFamily="heading"
          fontSize={{ base: "xl", md: "2xl" }}
          fontWeight="bold"
          color="neutral.900"
          letterSpacing="-0.5px"
          mb={4}
        >
          {t("intro.title")}
        </Text>

        <Box
          id={HELP_CENTER_IDS.guide.introCard}
          bg="white"
          border="1px solid"
          borderColor="neutral.100"
          borderRadius="2xl"
          p={{ base: 5, md: 8 }}
          shadow="0 1px 3px rgba(0,0,0,0.04)"
          mb={6}
        >
          <Text
            id={HELP_CENTER_IDS.guide.introDescription}
            fontSize="15px"
            color="neutral.600"
            lineHeight={1.8}
            mb={6}
          >
            {t("intro.description")}
          </Text>

          <Text
            id={HELP_CENTER_IDS.guide.introFeaturesTitle}
            fontFamily="heading"
            fontSize="md"
            fontWeight="600"
            color="neutral.800"
            mb={4}
          >
            {t("intro.features_title")}
          </Text>

          <Grid
            id={HELP_CENTER_IDS.guide.introFeaturesGrid}
            templateColumns={{
              base: "1fr",
              md: "repeat(2, 1fr)",
              lg: "repeat(3, 1fr)",
            }}
            gap={3}
          >
            {FEATURE_ICONS.map((IconComp, i) => (
              <Flex
                key={i}
                id={HELP_CENTER_IDS.guide.introFeature(i + 1)}
                align="center"
                gap={3}
                p={4}
                bg="neutral.50"
                borderRadius="xl"
                border="1px solid"
                borderColor="neutral.100"
                transition="all 0.2s ease"
                _hover={{
                  borderColor: "primary.200",
                  bg: "primary.50/50",
                  transform: "translateY(-2px)",
                }}
              >
                <Flex
                  align="center"
                  justify="center"
                  w={8}
                  h={8}
                  borderRadius="lg"
                  bg="primary.50"
                  flexShrink={0}
                >
                  <Icon as={IconComp} boxSize={4} color="primary.500" />
                </Flex>
                <Text fontSize="13px" color="neutral.700" lineHeight={1.4}>
                  {t(`intro.feature_${i + 1}`)}
                </Text>
              </Flex>
            ))}
          </Grid>
        </Box>
      </Box>

      {/* Showcase */}
      <Box
        id="guide-showcase"
        data-debug-id={HELP_CENTER_IDS.guide.showcaseDebug}
        scrollMarginTop="80px"
        mt={12}
      >
        <Flex
          id={HELP_CENTER_IDS.guide.showcaseLabelRow}
          align="center"
          gap={2}
          mb={3}
        >
          <Box
            id={HELP_CENTER_IDS.guide.showcaseLabelBar}
            w="20px"
            h="0.5"
            bg="primary.600"
            borderRadius="1px"
          />
          <Text
            id={HELP_CENTER_IDS.guide.showcaseLabel}
            fontSize="11px"
            fontWeight="bold"
            letterSpacing="0.12em"
            textTransform="uppercase"
            color="primary.600"
          >
            {t("showcase.label")}
          </Text>
        </Flex>

        <Text
          id={HELP_CENTER_IDS.guide.showcaseTitle}
          fontFamily="heading"
          fontSize={{ base: "xl", md: "2xl" }}
          fontWeight="bold"
          color="neutral.900"
          letterSpacing="-0.5px"
          mb={4}
        >
          {t("showcase.title")}
        </Text>

        <Text
          id={HELP_CENTER_IDS.guide.showcaseDescription}
          fontSize="15px"
          color="neutral.500"
          lineHeight={1.7}
          mb={6}
        >
          {t("showcase.description")}
        </Text>

        {/* Video */}
        <Box
          id={HELP_CENTER_IDS.guide.showcaseVideoCard}
          bg="white"
          border="1px solid"
          borderColor="neutral.200"
          borderRadius="2xl"
          overflow="hidden"
          shadow="0 4px 20px rgba(0,0,0,0.06)"
          mb={8}
        >
          <Box
            id={HELP_CENTER_IDS.guide.showcaseVideoContent}
            p={{ base: 3, md: 4 }}
          >
            <Text
              id={HELP_CENTER_IDS.guide.showcaseVideoTitle}
              fontSize="xs"
              fontWeight="600"
              color="neutral.400"
              textTransform="uppercase"
              letterSpacing="0.08em"
              mb={3}
            >
              {t("showcase.video_title")}
            </Text>
            <Box
              id={HELP_CENTER_IDS.guide.showcaseVideoPlayer}
              borderRadius="xl"
              overflow="hidden"
            >
              <WistiaPlayer
                mediaId={WISTIA_MEDIA_ID.introFull}
                aspect={16 / 9}
              />
            </Box>
          </Box>
        </Box>

        {/* Stats */}
        <Grid
          id={HELP_CENTER_IDS.guide.showcaseStatsGrid}
          templateColumns={{ base: "1fr", sm: "repeat(3, 1fr)" }}
          gap={4}
        >
          {(["cvs", "accuracy", "faster"] as const).map((key) => (
            <Box
              key={key}
              id={HELP_CENTER_IDS.guide.showcaseStat(key)}
              bg={key === "accuracy" ? "primary.500" : "neutral.900"}
              borderRadius="2xl"
              p={{ base: 5, md: 7 }}
              transition="transform 0.25s, box-shadow 0.25s"
              _hover={{
                transform: "translateY(-4px)",
                boxShadow: "0 16px 48px rgba(0,0,0,0.12)",
              }}
            >
              <Text
                fontFamily="heading"
                fontSize={{ base: "3xl", md: "4xl" }}
                fontWeight="800"
                letterSpacing="-2px"
                lineHeight={1}
                color="white"
                mb={2}
              >
                {t(`showcase.stats.${key}`)}
              </Text>
              <Text
                fontSize="12px"
                color={key === "accuracy" ? "whiteAlpha.800" : "whiteAlpha.600"}
              >
                {t(`showcase.stats.${key}_label`)}
              </Text>
            </Box>
          ))}
        </Grid>
      </Box>
    </Box>
  );
}
